import {
  isMeaningPracticeReady,
  meaningHasConfusablePractice,
  meaningHasEligibleAssessment,
  suitableExercisesForMode,
  isStandardAssessmentExercise,
  type PracticeExerciseMode,
} from "../exercises/index.ts";
import { answersMatch } from "../vocabulary/normalize.ts";
import type {
  Exercise,
  MeaningCard,
  MeaningPracticeState,
  ReviewEventKind,
  VocabularySnapshot,
  WrittenReviewEvent,
} from "../vocabulary/types.ts";
import { SCHEDULER_POLICY_VERSION } from "../vocabulary/types.ts";
import {
  classifyPresentation,
  isClockBackward,
  isDue,
  nextReviewAt as nextReviewAtFromStates,
  practiceStateFor,
  scheduleSnapshotOf,
  selectPracticeMeaning,
  nextSchedule,
} from "./scheduling.ts";

export type PracticeMode = PracticeExerciseMode;

export interface PracticeSelection {
  mode: PracticeMode;
  size: number;
  selectedMeaningIds: string[];
}

export interface EligibleMeaning {
  meaning: MeaningCard;
  state: MeaningPracticeState;
}

export type PracticePhase =
  | "question"
  | "saving"
  | "feedback"
  | "save_failed"
  | "conflict"
  | "clock_blocked";

export type AnswerOutcome = "correct" | "incorrect" | "unknown";

export interface PracticeQuestionSnapshot {
  meaningId: string;
  exercise: Exercise;
  attempt: "first" | "retry";
  presentedAt: string;
  contentRevision: number;
  expectedStateRevision: number;
  wasDue: boolean;
  role: "assessment" | "supporting";
  bucket: "scheduled" | "extra";
}

export interface FirstAttemptRecord {
  meaningId: string;
  question: string;
  givenAnswer: string | null;
  unknown: boolean;
  correct: boolean;
  hintUsed: boolean;
  role: "assessment" | "supporting";
  correctAnswer: string;
  explanation: string;
  relatedWordDistinctions: Exercise["relatedWordDistinctions"];
  wordDisplay: string;
  type: MeaningCard["type"];
  shortLabel: string;
  previousStage?: number;
  nextStage?: number;
  previousDueAt?: string;
  nextDueAt?: string;
}

export interface PracticeSessionState {
  sessionId: string;
  mode: PracticeMode;
  startedAt: string;
  vocabularyRevision: number;
  selectedMeaningIds: string[];
  maxMeanings: number;
  current?: PracticeQuestionSnapshot;
  phase: PracticePhase;
  lastOutcome?: AnswerOutcome;
  pendingEvent?: WrittenReviewEvent;
  assessedMeaningIds: string[];
  completedItemCount: number;
  retryCount: number;
  firstAttempts: FirstAttemptRecord[];
  ended: boolean;
  endedExplicitly: boolean;
  unsavedExit: boolean;
  clockMessage?: string;
  conflictMessage?: string;
  persistError?: string;
}

export interface PracticeEngineDeps {
  random: () => number;
  createId: () => string;
  now: () => string;
}

function pickIndex(random: () => number, length: number): number {
  if (length <= 0) {
    return 0;
  }
  return Math.floor(random() * length) % length;
}

export function chooseExercise(
  exercises: readonly Exercise[],
  lastExerciseId: string | null,
  random: () => number,
): Exercise | undefined {
  if (exercises.length === 0) {
    return undefined;
  }
  const alternatives = lastExerciseId
    ? exercises.filter((exercise) => exercise.id !== lastExerciseId)
    : exercises;
  const pool = alternatives.length > 0 ? alternatives : exercises;
  return pool[pickIndex(random, pool.length)];
}

export function eligibleMeanings(
  snapshot: VocabularySnapshot,
  mode: PracticeMode,
  selectedMeaningIds: readonly string[] = [],
  now?: string,
): EligibleMeaning[] {
  const selected = new Set(selectedMeaningIds);
  const at = now ?? snapshot.schedulerState.lastObservedAt ?? snapshot.meanings[0]?.createdAt ?? "1970-01-01T00:00:00.000Z";
  return snapshot.meanings
    .filter((meaning) => {
      if (!isMeaningPracticeReady(snapshot, meaning)) {
        return false;
      }
      if (mode === "confusable") {
        return meaningHasConfusablePractice(snapshot, meaning);
      }
      if (mode === "select") {
        return selected.has(meaning.id) && suitableExercisesForMode(snapshot, meaning.id, mode).length > 0;
      }
      if (mode === "due") {
        return (
          meaningHasEligibleAssessment(snapshot, meaning) &&
          isDue(practiceStateFor(snapshot, meaning.id).dueAt, at)
        );
      }
      return suitableExercisesForMode(snapshot, meaning.id, mode).length > 0;
    })
    .map((meaning) => ({
      meaning,
      state: practiceStateFor(snapshot, meaning.id),
    }));
}

export function dueEligibleMeanings(
  snapshot: VocabularySnapshot,
  now: string,
): EligibleMeaning[] {
  return snapshot.meanings
    .filter((meaning) => {
      if (!isMeaningPracticeReady(snapshot, meaning) || !meaningHasEligibleAssessment(snapshot, meaning)) {
        return false;
      }
      return isDue(practiceStateFor(snapshot, meaning.id).dueAt, now);
    })
    .map((meaning) => ({ meaning, state: practiceStateFor(snapshot, meaning.id) }));
}

export function extraEligibleMeanings(snapshot: VocabularySnapshot): EligibleMeaning[] {
  return snapshot.meanings
    .filter(
      (meaning) =>
        isMeaningPracticeReady(snapshot, meaning) &&
        suitableExercisesForMode(snapshot, meaning.id, "extra").length > 0,
    )
    .map((meaning) => ({ meaning, state: practiceStateFor(snapshot, meaning.id) }));
}

export function unavailableOverdueMeanings(snapshot: VocabularySnapshot, now: string): MeaningCard[] {
  return snapshot.meanings.filter((meaning) => {
    const state = practiceStateFor(snapshot, meaning.id);
    if (!isDue(state.dueAt, now)) {
      return false;
    }
    return !isMeaningPracticeReady(snapshot, meaning) || !meaningHasEligibleAssessment(snapshot, meaning);
  });
}

export function nextReviewTime(snapshot: VocabularySnapshot, now: string): string | null {
  const ready = extraEligibleMeanings(snapshot).map((item) => item.state);
  return nextReviewAtFromStates(ready, now);
}

export function validateSessionSize(size: number): { ok: true } | { ok: false; message: string } {
  if (!Number.isInteger(size) || size < 1) {
    return { ok: false, message: "Session size must be a whole number of at least 1." };
  }
  return { ok: true };
}

export function checkTypedAnswer(exercise: Exercise, value: string): AnswerOutcome {
  return answersMatch(value, exercise.acceptedAnswers) ? "correct" : "incorrect";
}

export function checkChoiceAnswer(exercise: Exercise, optionId: string): AnswerOutcome {
  if (exercise.kind !== "confusable_context") {
    return "incorrect";
  }
  return exercise.correctOptionId === optionId ? "correct" : "incorrect";
}

function candidatesForMode(
  snapshot: VocabularySnapshot,
  mode: PracticeMode,
  selectedMeaningIds: readonly string[],
  excludedMeaningIds: readonly string[],
  now: string,
): MeaningPracticeState[] {
  const excluded = new Set(excludedMeaningIds);
  const selected = new Set(selectedMeaningIds);
  return snapshot.meanings
    .filter((meaning) => {
      if (excluded.has(meaning.id)) {
        return false;
      }
      if (!isMeaningPracticeReady(snapshot, meaning)) {
        return false;
      }
      if (suitableExercisesForMode(snapshot, meaning.id, mode).length === 0) {
        return false;
      }
      if (mode === "select") {
        return selected.has(meaning.id);
      }
      if (mode === "confusable") {
        return meaningHasConfusablePractice(snapshot, meaning);
      }
      if (mode === "due") {
        return (
          meaningHasEligibleAssessment(snapshot, meaning) &&
          isDue(practiceStateFor(snapshot, meaning.id).dueAt, now)
        );
      }
      return true;
    })
    .map((meaning) => practiceStateFor(snapshot, meaning.id));
}

function presentQuestion(
  snapshot: VocabularySnapshot,
  selection: PracticeSelection,
  excludedMeaningIds: readonly string[],
  deps: PracticeEngineDeps,
  attempt: "first" | "retry",
  meaningId?: string,
): PracticeQuestionSnapshot | undefined {
  const now = deps.now();
  const chosenId =
    meaningId ??
    selectPracticeMeaning(
      candidatesForMode(snapshot, selection.mode, selection.selectedMeaningIds, excludedMeaningIds, now),
      now,
      snapshot.schedulerState.lastPracticedMeaningId,
      selection.mode !== "due",
    )?.meaningId;
  if (!chosenId) {
    return undefined;
  }
  const meaning = snapshot.meanings.find((item) => item.id === chosenId);
  if (!meaning) {
    return undefined;
  }
  const state = practiceStateFor(snapshot, chosenId);
  const exercises = suitableExercisesForMode(snapshot, chosenId, selection.mode);
  const exercise = chooseExercise(exercises, state.lastExerciseId, deps.random);
  if (!exercise) {
    return undefined;
  }
  const classification = classifyPresentation(state, now);
  const isRetry = attempt === "retry";
  const role: "assessment" | "supporting" =
    isRetry || !isStandardAssessmentExercise(exercise) ? "supporting" : "assessment";
  return {
    meaningId: chosenId,
    exercise,
    attempt,
    presentedAt: now,
    contentRevision: meaning.contentRevision,
    expectedStateRevision: state.stateRevision,
    wasDue: classification.wasDue,
    role,
    bucket: classification.wasDue ? "scheduled" : "extra",
  };
}

export function startSession(
  snapshot: VocabularySnapshot,
  selection: PracticeSelection,
  deps: PracticeEngineDeps,
):
  | { ok: true; session: PracticeSessionState }
  | { ok: false; message: string; nextReviewAt?: string } {
  const sizeCheck = validateSessionSize(selection.size);
  if (!sizeCheck.ok) {
    return sizeCheck;
  }
  const now = deps.now();
  const eligible = candidatesForMode(snapshot, selection.mode, selection.selectedMeaningIds, [], now);
  if (eligible.length === 0) {
    if (selection.mode === "due") {
      return {
        ok: false,
        message: "No reviews due.",
        nextReviewAt: nextReviewTime(snapshot, now) ?? undefined,
      };
    }
    return { ok: false, message: "No meanings are available for this mode." };
  }
  const question = presentQuestion(snapshot, selection, [], deps, "first");
  if (!question) {
    return { ok: false, message: "A selected meaning does not have a suitable stored exercise." };
  }
  return {
    ok: true,
    session: {
      sessionId: deps.createId(),
      mode: selection.mode,
      startedAt: now,
      vocabularyRevision: snapshot.dataRevision,
      selectedMeaningIds: selection.selectedMeaningIds,
      maxMeanings: Math.min(selection.size, eligible.length),
      current: question,
      phase: "question",
      assessedMeaningIds: [],
      completedItemCount: 0,
      retryCount: 0,
      firstAttempts: [],
      ended: false,
      endedExplicitly: false,
      unsavedExit: false,
    },
  };
}

export interface SubmitAnswerInput {
  value?: string;
  optionId?: string;
  unknown?: boolean;
  hintUsed?: boolean;
}

function recordFirstAttempt(
  snapshot: VocabularySnapshot,
  current: PracticeQuestionSnapshot,
  outcome: AnswerOutcome,
  givenAnswer: string | null,
  hintUsed: boolean,
): FirstAttemptRecord {
  const meaning = snapshot.meanings.find((item) => item.id === current.meaningId);
  const word = snapshot.words.find((item) => item.id === meaning?.wordId);
  return {
    meaningId: current.meaningId,
    question: current.exercise.question,
    givenAnswer,
    unknown: outcome === "unknown",
    correct: outcome === "correct",
    hintUsed,
    role: current.role,
    correctAnswer: current.exercise.correctAnswer,
    explanation: current.exercise.explanation,
    relatedWordDistinctions: current.exercise.relatedWordDistinctions,
    wordDisplay: word?.displayText ?? "",
    type: meaning?.type ?? "other",
    shortLabel: meaning?.shortLabel ?? "",
  };
}

function buildReviewEvent(
  snapshot: VocabularySnapshot,
  session: PracticeSessionState,
  current: PracticeQuestionSnapshot,
  outcome: AnswerOutcome,
  hintUsed: boolean,
  completedAt: string,
  eventId: string,
  eventKind: ReviewEventKind,
): WrittenReviewEvent {
  const state = practiceStateFor(snapshot, current.meaningId);
  const classification = classifyPresentation(state, current.presentedAt);
  const schedule = nextSchedule(state, {
    eventKind,
    outcome,
    hintUsed,
    classification,
    completedAt,
  });
  const resulting: MeaningPracticeState = {
    ...state,
    stage: schedule.stage,
    dueAt: schedule.dueAt,
    lastExposureAt: eventKind === "invalidation" && outcome == null ? state.lastExposureAt : completedAt,
    lastAssessmentAt: eventKind === "assessment" ? completedAt : state.lastAssessmentAt,
    lastExerciseId: current.exercise.id,
    stateRevision: state.stateRevision + 1,
  };
  return {
    format: "written",
    origin: "exercise",
    eventId,
    sessionId: session.sessionId,
    meaningId: current.meaningId,
    exerciseId: current.exercise.id,
    exerciseKind: current.exercise.kind,
    meaningContentRevision: current.contentRevision,
    expectedStateRevision: current.expectedStateRevision,
    presentedAt: current.presentedAt,
    completedAt,
    eventKind,
    firstAttemptOutcome: outcome,
    hintUsed,
    scheduledOrExtra: current.bucket,
    schedulerVersion: SCHEDULER_POLICY_VERSION,
    previousSchedule: scheduleSnapshotOf(state),
    resultingSchedule: scheduleSnapshotOf(resulting),
  };
}

export function submitAnswer(
  snapshot: VocabularySnapshot,
  session: PracticeSessionState,
  input: SubmitAnswerInput,
  deps: PracticeEngineDeps,
): {
  session: PracticeSessionState;
  event?: WrittenReviewEvent;
  outcome: AnswerOutcome;
} {
  if (!session.current || session.phase !== "question" || session.ended) {
    return { session, outcome: session.lastOutcome ?? "incorrect" };
  }
  const current = session.current;
  const completedAt = deps.now();
  if (isClockBackward(snapshot.schedulerState.lastObservedAt, current.presentedAt, completedAt)) {
    return {
      outcome: session.lastOutcome ?? "incorrect",
      session: {
        ...session,
        phase: "clock_blocked",
        clockMessage:
          "This device clock moved backwards. Practice results are paused until the time looks consistent.",
      },
    };
  }
  let outcome: AnswerOutcome = "incorrect";
  let givenAnswer: string | null = input.value?.trim() || null;
  if (input.unknown) {
    outcome = "unknown";
    givenAnswer = null;
  } else if (current.exercise.kind === "confusable_context") {
    outcome = checkChoiceAnswer(current.exercise, input.optionId ?? "");
    givenAnswer =
      current.exercise.options.find((option) => option.id === input.optionId)?.label ?? givenAnswer;
  } else {
    outcome = checkTypedAnswer(current.exercise, input.value ?? "");
  }
  const hintUsed = Boolean(input.hintUsed);
  const eventKind: ReviewEventKind = current.role === "assessment" ? "assessment" : "supporting";
  const event = buildReviewEvent(
    snapshot,
    session,
    current,
    outcome,
    hintUsed,
    completedAt,
    deps.createId(),
    eventKind,
  );
  const isFirst = current.attempt === "first";
  const firstAttempts = isFirst
    ? [...session.firstAttempts, recordFirstAttempt(snapshot, current, outcome, givenAnswer, hintUsed)]
    : session.firstAttempts;
  return {
    outcome,
    event,
    session: {
      ...session,
      phase: "saving",
      lastOutcome: outcome,
      pendingEvent: event,
      firstAttempts,
      persistError: undefined,
      clockMessage: undefined,
    },
  };
}

export function reportQuestionProblem(
  snapshot: VocabularySnapshot,
  session: PracticeSessionState,
  deps: PracticeEngineDeps,
): { session: PracticeSessionState; event?: WrittenReviewEvent; correctionOfEventId?: string } {
  if (!session.current || session.ended) {
    return { session };
  }
  const current = session.current;
  if (session.phase === "question") {
    const completedAt = deps.now();
    if (isClockBackward(snapshot.schedulerState.lastObservedAt, current.presentedAt, completedAt)) {
      return {
        session: {
          ...session,
          phase: "clock_blocked",
          clockMessage:
            "This device clock moved backwards. Practice results are paused until the time looks consistent.",
        },
      };
    }
    const event: WrittenReviewEvent = {
      format: "written",
      origin: "exercise",
      eventId: deps.createId(),
      sessionId: session.sessionId,
      meaningId: current.meaningId,
      exerciseId: current.exercise.id,
      exerciseKind: current.exercise.kind,
      meaningContentRevision: current.contentRevision,
      expectedStateRevision: current.expectedStateRevision,
      presentedAt: current.presentedAt,
      completedAt,
      eventKind: "invalidation",
      firstAttemptOutcome: null,
      hintUsed: false,
      scheduledOrExtra: current.bucket,
      schedulerVersion: SCHEDULER_POLICY_VERSION,
      previousSchedule: scheduleSnapshotOf(practiceStateFor(snapshot, current.meaningId)),
      resultingSchedule: scheduleSnapshotOf(practiceStateFor(snapshot, current.meaningId)),
    };
    return {
      event,
      session: {
        ...session,
        phase: "saving",
        pendingEvent: event,
        lastOutcome: undefined,
      },
    };
  }
  if (session.phase === "feedback" && session.pendingEvent?.eventKind === "assessment") {
    const completedAt = deps.now();
    const correction: WrittenReviewEvent = {
      format: "written",
      origin: "exercise",
      eventId: deps.createId(),
      sessionId: session.sessionId,
      meaningId: current.meaningId,
      exerciseId: current.exercise.id,
      exerciseKind: current.exercise.kind,
      meaningContentRevision: current.contentRevision,
      expectedStateRevision: practiceStateFor(snapshot, current.meaningId).stateRevision,
      presentedAt: current.presentedAt,
      completedAt,
      eventKind: "invalidation",
      firstAttemptOutcome: null,
      hintUsed: false,
      scheduledOrExtra: current.bucket,
      schedulerVersion: SCHEDULER_POLICY_VERSION,
      previousSchedule: session.pendingEvent.resultingSchedule,
      resultingSchedule: session.pendingEvent.previousSchedule,
    };
    return {
      event: correction,
      correctionOfEventId: session.pendingEvent.eventId,
      session: {
        ...session,
        phase: "saving",
        pendingEvent: correction,
      },
    };
  }
  return { session };
}

export function markSaveSucceeded(
  session: PracticeSessionState,
  snapshot: VocabularySnapshot,
): PracticeSessionState {
  const event = session.pendingEvent;
  const firstAttempts =
    event?.format === "written" && session.current?.attempt === "first"
      ? session.firstAttempts.map((item, index) =>
          index === session.firstAttempts.length - 1 && item.meaningId === event.meaningId
            ? {
                ...item,
                previousStage: event.previousSchedule.stage,
                nextStage: event.resultingSchedule.stage,
                previousDueAt: event.previousSchedule.dueAt,
                nextDueAt: event.resultingSchedule.dueAt,
              }
            : item,
        )
      : session.firstAttempts;
  const assessedMeaningIds =
    session.current && !session.assessedMeaningIds.includes(session.current.meaningId)
      ? [...session.assessedMeaningIds, session.current.meaningId]
      : session.assessedMeaningIds;
  return {
    ...session,
    phase: "feedback",
    vocabularyRevision: snapshot.dataRevision,
    persistError: undefined,
    completedItemCount: session.completedItemCount + 1,
    firstAttempts,
    assessedMeaningIds,
  };
}

export function markSaveFailed(session: PracticeSessionState, message: string): PracticeSessionState {
  return {
    ...session,
    phase: "save_failed",
    persistError: message,
  };
}

export function markConflict(session: PracticeSessionState, message: string): PracticeSessionState {
  return {
    ...session,
    phase: "conflict",
    conflictMessage: message,
  };
}

export function advanceAfterFeedback(
  snapshot: VocabularySnapshot,
  session: PracticeSessionState,
  deps: PracticeEngineDeps,
): PracticeSessionState {
  if (!session.current || session.phase !== "feedback") {
    return session;
  }
  if (session.assessedMeaningIds.length >= session.maxMeanings) {
    return endSession(session, false);
  }
  const next = presentQuestion(
    snapshot,
    {
      mode: session.mode,
      size: session.maxMeanings,
      selectedMeaningIds: session.selectedMeaningIds,
    },
    session.assessedMeaningIds,
    deps,
    "first",
  );
  if (!next) {
    return endSession(
      {
        ...session,
        pendingEvent: undefined,
        lastOutcome: undefined,
        current: undefined,
      },
      false,
    );
  }
  return {
    ...session,
    current: next,
    phase: "question",
    pendingEvent: undefined,
    lastOutcome: undefined,
    persistError: undefined,
    vocabularyRevision: snapshot.dataRevision,
  };
}

export function practiseAgain(
  snapshot: VocabularySnapshot,
  session: PracticeSessionState,
  deps: PracticeEngineDeps,
): PracticeSessionState {
  if (!session.current || session.phase !== "feedback") {
    return session;
  }
  const retry = presentQuestion(
    snapshot,
    {
      mode: session.mode === "due" ? "extra" : session.mode,
      size: session.maxMeanings,
      selectedMeaningIds: session.selectedMeaningIds.length
        ? session.selectedMeaningIds
        : [session.current.meaningId],
    },
    [],
    deps,
    "retry",
    session.current.meaningId,
  );
  if (!retry) {
    return session;
  }
  return {
    ...session,
    current: retry,
    phase: "question",
    pendingEvent: undefined,
    lastOutcome: undefined,
    retryCount: session.retryCount + 1,
    persistError: undefined,
  };
}

export function endSession(session: PracticeSessionState, explicit: boolean): PracticeSessionState {
  const unsaved = session.phase === "saving" || session.phase === "save_failed";
  return {
    ...session,
    ended: true,
    endedExplicitly: explicit,
    current: unsaved ? session.current : undefined,
    unsavedExit: unsaved,
  };
}

export interface SummaryItem {
  meaningId: string;
  wordDisplay: string;
  type: MeaningCard["type"];
  shortLabel: string;
  review?: FirstAttemptRecord;
}

export interface PracticeSummary {
  correctCount: number;
  reviewCount: number;
  supportingCount: number;
  correct: SummaryItem[];
  needsReview: SummaryItem[];
  supporting: SummaryItem[];
  unattempted: SummaryItem[];
}

export function buildSummary(
  snapshot: VocabularySnapshot,
  session: PracticeSessionState,
): PracticeSummary {
  const attempted = session.firstAttempts;
  const assessments = attempted.filter((item) => item.role === "assessment");
  const supporting = attempted.filter((item) => item.role === "supporting");
  const correct = assessments.filter((item) => item.correct && !item.hintUsed);
  const needsReview = assessments.filter((item) => !item.correct || item.hintUsed);
  const attemptedIds = new Set(attempted.map((item) => item.meaningId));
  const unattemptedIds = session.endedExplicitly
    ? session.selectedMeaningIds.filter((id) => !attemptedIds.has(id))
    : [];
  const toItem = (meaningId: string, review?: FirstAttemptRecord): SummaryItem => {
    if (review) {
      return {
        meaningId,
        wordDisplay: review.wordDisplay,
        type: review.type,
        shortLabel: review.shortLabel,
        review,
      };
    }
    const meaning = snapshot.meanings.find((item) => item.id === meaningId);
    const word = snapshot.words.find((item) => item.id === meaning?.wordId);
    return {
      meaningId,
      wordDisplay: word?.displayText ?? "",
      type: meaning?.type ?? "other",
      shortLabel: meaning?.shortLabel ?? "",
    };
  };
  return {
    correctCount: correct.length,
    reviewCount: needsReview.length,
    supportingCount: supporting.length,
    correct: correct.map((item) => toItem(item.meaningId, item)),
    needsReview: needsReview.map((item) => toItem(item.meaningId, item)),
    supporting: supporting.map((item) => toItem(item.meaningId, item)),
    unattempted: unattemptedIds.map((id) => toItem(id)),
  };
}

export function progressLabel(session: PracticeSessionState): {
  completed: number;
  total: number;
  retries: number;
} {
  return {
    completed: session.completedItemCount,
    total: session.maxMeanings,
    retries: session.retryCount,
  };
}

export { practiceStateFor };
